import { BigNumber, ethers } from "ethers";
import { useEffect, useMemo, useState } from "react";
import { useBetween } from "use-between";

import { useWeb3React } from "@web3-react/core";

import {
  GNARLY_GOATS_CONTRACT_ADDRESSES,
  GNARLY_GOATS_MINT_PASS_CONTRACT_ADDRESSES
} from "../constants/addresses";
import GnarlyGoatsContract from "../contracts/GnarlyGoatsContract.json";
import GnarlyGoatsMintPassContract from "../contracts/GnarlyGoatsMintPassContract.json";

export function useGnarlyGoatsContracts() {
	const { library, account, chainId } = useWeb3React()

	const gnarlyGoatsContract = useMemo(() => {
		if (!GNARLY_GOATS_CONTRACT_ADDRESSES || !!!library) {
			return null
		}

		try {
			if (account && chainId) {
				return new ethers.Contract(
					GNARLY_GOATS_CONTRACT_ADDRESSES[chainId as number],
					GnarlyGoatsContract.abi,
					library.getSigner(account as string).connectUnchecked()
				)
			}
			/**
			 * If unable to get signer (wallet is not connected),
			 * then use default provider for the desired network
			 */
			return new ethers.Contract(
				GNARLY_GOATS_CONTRACT_ADDRESSES[chainId as number],
				GnarlyGoatsContract.abi,
				ethers.getDefaultProvider('rinkeby')
			)
		} catch (error) {
			console.error('Failed to initialize Gnarly Goats contract.')
			return null
		}
	}, [account, chainId])

	const mintPassContract = useMemo(() => {
		if (!GNARLY_GOATS_MINT_PASS_CONTRACT_ADDRESSES || !!!library) {
			return null
		}

		try {
			if (account && chainId) {
				return new ethers.Contract(
					GNARLY_GOATS_MINT_PASS_CONTRACT_ADDRESSES[chainId as number],
					GnarlyGoatsMintPassContract.abi,
					library.getSigner(account as string).connectUnchecked()
				)
			}
			/**
			 * If unable to get signer (wallet is not connected),
			 * then use default provider for the desired network
			 */
			return new ethers.Contract(
				GNARLY_GOATS_MINT_PASS_CONTRACT_ADDRESSES[chainId as number],
				GnarlyGoatsMintPassContract.abi,
				ethers.getDefaultProvider('rinkeby')
			)
		} catch (error) {
			console.error('Failed to initialize Mint Pass contract')
			return null
		}
	}, [account, chainId])

	return { contract: gnarlyGoatsContract, mintPassContract }
}

export function useGnarlyGoatsContractsState() {
	const { library, account, chainId } = useWeb3React()
	const { contract, mintPassContract } = useGnarlyGoatsContracts()

	const [owner, setOwner] = useState<string>()
	const [mintPassContractOwner, setMintPassContractOwner] = useState<string>()

	const [circSupplyOfGoats, setCircSupplyOfGoats] = useState<number>(0)
	const [mintPassTotalSupply, setMintPassTotalSupply] = useState<number>(0)

	const [accountBalance, setAccountBalance] = useState<number>(0)
	const [accountEtherBalance, setAccountEtherBalance] = useState<number>(0)
	const [
		historicalAccountMintPassBalance,
		setHistoricalAccountMintPassBalance,
	] = useState<number>(0)

	const [gnarlyGoatsClaimed, setGnarlyGoatsClaimed] = useState<number>(0)

	const [mintPassesClaimed, setMintPassesClaimed] = useState<number>(0)
	const [mintPassesAvailable, setMintPassesAvailable] = useState<number>(0)

	const [accountMintPassBalance, setAccountMintPassBalance] =
		useState<number>(0)

	const [baseUri, setBaseUri] = useState<string>()
	const [mintPassContractBaseUri, setMintPassContractBaseUri] =
		useState<string>()

	useEffect(() => {
		if (!contract || !mintPassContract) {
			return
		}

		try {
			setContractTotalSupply()
			setMintPassContractTotalSupply()
			fetchContractOwners()
			fetchEtherBalance()
			fetchAccountBalance()
			fetchAccountMintPassBalance()
			fetchAccountHistoricalMintPassBalance()
			fetchGnarlyGoatsClaimed()
			fetchMintPassesClaimed()
			fetchMintPassesAvailable()
			fetchMintPassBaseUri()
			fetchBaseUri()
		} catch (err) {
			console.log('Error fetching contract state.')
		}
	}, [library, account, chainId, contract])

	const setContractTotalSupply = async () => {
		try {
			if (contract) {
				let supply: number = await contract.totalSupply()
				if (supply > 0) {
					setCircSupplyOfGoats(supply)
				}
			}
		} catch (err) {
			console.log('Could not fetch contract total supply: ', err)
		}
	}

	const setMintPassContractTotalSupply = async () => {
		setMintPassTotalSupply(await mintPassContract?.totalSupply())
	}

	const fetchContractOwners = async () => {
		setOwner(await contract?.owner())
		setMintPassContractOwner(await mintPassContract?.owner())
	}

	const fetchEtherBalance = async () => {
		const balance = (await library.getBalance(account as string)) as BigNumber
		const balanceInEther = ethers.utils.formatEther(balance)
		setAccountEtherBalance(Number(balanceInEther))
	}

	const fetchAccountBalance = async () => {
		const balance = await contract?.balanceOf(account)
		setAccountBalance(balance)
	}

	const fetchAccountMintPassBalance = async () => {
		const balance = await mintPassContract?.balanceOf(account)
		setAccountMintPassBalance(Number((balance as BigNumber).toString()))
	}

	const fetchAccountHistoricalMintPassBalance = async () => {
		const balance = await mintPassContract?.historicalBalanceOf(account)
		setHistoricalAccountMintPassBalance(
			Number((balance as BigNumber).toString())
		)
	}

	const fetchGnarlyGoatsClaimed = async () => {
		const balance = await contract?.amountClaimed()
		setGnarlyGoatsClaimed(Number((balance as BigNumber).toString()))
	}

	const fetchMintPassesClaimed = async () => {
		const balance = await mintPassContract?.amountClaimed()
		setMintPassesClaimed(Number((balance as BigNumber).toString()))
	}

	const fetchMintPassesAvailable = async () => {
		const balance = await mintPassContract?.amountAvailable()
		setMintPassesAvailable(Number((balance as BigNumber).toString()))
	}

	const fetchBaseUri = async () => {
		const uri = await contract?.baseURI()
		setBaseUri(uri)
	}

	const updateBaseUri = async (uri: string) => {
		await contract?.setBaseURI(uri)
	}

	const fetchMintPassBaseUri = async () => {
		const uri = await mintPassContract?.baseURI()
		setMintPassContractBaseUri(uri)
	}

	const updateMintPassContractBaseUri = async (uri: string) => {
		await mintPassContract?.setBaseURI(uri)
	}

	const isOwner = account && owner && account === owner

	return {
		isOwner,
		mintPassContractOwner,
		circSupplyOfGoats,
		totalSupplyOfPasses: mintPassTotalSupply,
		accountEtherBalance,
		accountBalance,
		accountMintPassBalance,
		fetchAccountMintPassBalance,
		historicalAccountMintPassBalance,
		gnarlyGoatsClaimed,
		mintPassesClaimed,
		mintPassesAvailable,
		mintPassContractBaseUri,
		updateMintPassContractBaseUri,
		baseUri,
		updateBaseUri,
	}
}

export const useSharedContractsState = () =>
	useBetween(useGnarlyGoatsContractsState)
